// Crypto-wallet status axis — the lifecycle of a row in `investor_crypto_wallets`.
//
// Distinct from every other status this module shows:
//   - VASP_USER_STATUS (@shared/constants/vaspStatus) is the *registration*
//     axis: whether Ctrl-Alt has accepted the investor's organisation. A wallet
//     only exists once that reached `active`.
//   - TRUSTLINE status (CryptoWallet.trustlineStatus) is the per-token axis on
//     `investor_crypto_wallet_trustlines` — a different table entirely.
// This one answers "has the wallet itself been validated on-chain?".
//
// Values mirror admin-service's CRYPTO_WALLET_STATUS enum exactly; those four
// labels are the only ones the `crypto_wallet_status` DB enum accepts.
export const CRYPTO_WALLET_STATUS = {
  PENDING_VALIDATION: 'pending_validation',
  ACTIVE: 'active',
  VALIDATION_FAILED: 'validation_failed',
  DEACTIVATED: 'deactivated',
} as const

export type CryptoWalletStatus = (typeof CRYPTO_WALLET_STATUS)[keyof typeof CRYPTO_WALLET_STATUS]

// How the wallet came to exist — provenance, NOT lifecycle. Orthogonal to
// CRYPTO_WALLET_STATUS above: a `manual` wallet can be `active`, and an
// `xrpl_custody` one can be `pending_validation`.
//
//   xrpl_custody — the current path. admin-service derives the key pair from the
//                  investor id and funds the account on XRPL, then hands the
//                  address to Ctrl-Alt during approve. A real ledger account the
//                  broker can sign for.
//   vasp         — historical. Minted by the older approve flow, whose address
//                  was an unowned placeholder: it passed Ctrl-Alt's format check
//                  but had no key pair behind it.
//   manual       — the crypto-readiness toggle. No address at all, so nothing
//                  can be signed or reconciled.
//
// Do NOT gate the Sync action on this field — `vaspReferenceId` is the actual
// precondition. See canSync in components/VaspWalletQueueList.vue.
export const WALLET_VALIDATION_METHOD = {
  VASP: 'vasp',
  MANUAL: 'manual',
  XRPL_CUSTODY: 'xrpl_custody',
} as const

export type WalletValidationMethod =
  (typeof WALLET_VALIDATION_METHOD)[keyof typeof WALLET_VALIDATION_METHOD]

// OCP lookup keyed by the raw string, so a value the UI does not know falls back
// to a formatted label rather than rendering blank.
const WALLET_VALIDATION_METHOD_LABEL_MAP: Record<string, string> = {
  [WALLET_VALIDATION_METHOD.VASP]: 'VAISP',
  [WALLET_VALIDATION_METHOD.MANUAL]: 'Manual',
  [WALLET_VALIDATION_METHOD.XRPL_CUSTODY]: 'Broker custody',
}

export function walletValidationMethodLabel(method: string | null | undefined): string {
  if (!method) return 'Unknown'
  return WALLET_VALIDATION_METHOD_LABEL_MAP[method] ?? method
}

// StatusBadge color per wallet status — OCP lookup (coding-principles §9.4)
// keyed by the raw string so an unknown or newly added server value resolves to
// neutral instead of rendering an empty badge.
type CryptoWalletStatusColor = 'success' | 'warning' | 'danger' | 'neutral'

export const CRYPTO_WALLET_STATUS_COLOR_MAP: Record<string, CryptoWalletStatusColor> = {
  [CRYPTO_WALLET_STATUS.ACTIVE]: 'success',
  [CRYPTO_WALLET_STATUS.PENDING_VALIDATION]: 'warning',
  [CRYPTO_WALLET_STATUS.VALIDATION_FAILED]: 'danger',
  [CRYPTO_WALLET_STATUS.DEACTIVATED]: 'neutral',
}

export function cryptoWalletStatusColor(status: string | undefined): CryptoWalletStatusColor {
  if (!status) return 'neutral'
  return CRYPTO_WALLET_STATUS_COLOR_MAP[status] ?? 'neutral'
}

// ── VAISP wallet queue filter cards ──────────────────────────────────────────
// The 4 status-filter cards above the queue (All entries · Pending validation ·
// Active · Inactive). Each card maps to the set of `walletStatus` values it
// represents; clicking one narrows the list to those statuses.
//
// `inactive` deliberately covers two statuses: a wallet the vendor refused and
// one an admin switched off are both "not usable", and splitting them into two
// cards would leave one of them showing 0 almost always. The buckets are
// disjoint and cover the enum, so `all` is their sum — which is what lets the
// counts endpoint derive `all` server-side in a single GROUP BY.
export type VaspWalletCardKey = 'all' | 'pending' | 'active' | 'inactive'

export const VASP_WALLET_CARD_STATUS_FILTERS: Record<VaspWalletCardKey, CryptoWalletStatus[]> = {
  // Empty means "send no status filter" rather than "list the four we know
  // about": if the server ever grows a fifth status, an explicit list would
  // silently hide those rows from the card that promises to show everything.
  all: [],
  pending: [CRYPTO_WALLET_STATUS.PENDING_VALIDATION],
  active: [CRYPTO_WALLET_STATUS.ACTIVE],
  inactive: [CRYPTO_WALLET_STATUS.VALIDATION_FAILED, CRYPTO_WALLET_STATUS.DEACTIVATED],
}
